import React from "react";
import U2000Label from "../label/U2000Label";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

interface U2000InputTextBaseProps {
  fieldName: string;
  label?: string;
  placeholder?: string;
  type?: "string" | "number" | "email" | "password" | "tel";
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  value?: string | number;
  setValue?: (value: string | number) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  error?: string;
  icon?: React.ReactNode;
}

// Classes CSS modernes pour les états des inputs
const getInputClasses = (isFocused: boolean, hasError: boolean, readonly?: boolean, disabled?: boolean) => {
  const baseClasses = "w-full px-4 py-3 text-sm border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2";

  if (disabled) {
    return `${baseClasses} bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (readonly) {
    return `${baseClasses} bg-gray-50 text-gray-700 border-gray-200 cursor-text`;
  }

  if (hasError) {
    return `${baseClasses} bg-white border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  if (isFocused) {
    return `${baseClasses} bg-white border-blue-500 ring-blue-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  return `${baseClasses} bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 text-gray-900 placeholder:text-gray-400 hover:border-gray-400`;
};

const U2000InputTextBase: React.FC<U2000InputTextBaseProps> = ({
  fieldName,
  placeholder,
  label,
  type = "string",
  disabled = false,
  readonly = false,
  required = false,
  value,
  setValue,
  onFocus,
  onBlur,
  onKeyDown,
  inputRef,
  error,
  icon,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasError = !!error;

  const inputType = type === "string" ? "text" : type;

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    u2000SelectInputContentsOnFocus(e);
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    let newValue: any = e.target.value;
    if (type === "number") {
      newValue = e.target.value ? parseFloat(e.target.value) : "";
    }
    setValue && setValue(newValue);
  }

  const inputClasses = `${getInputClasses(isFocused, hasError, readonly, disabled)} ${icon ? "pl-10" : ""}`;

  return (
    <div className="space-y-1.5">
      <U2000Label label={label} required={required} htmlFor={fieldName} />

      <div className="relative">
        {icon && <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">{icon}</div>}

        <input
          ref={inputRef}
          type={inputType}
          id={fieldName}
          name={fieldName}
          placeholder={placeholder}
          className={inputClasses}
          value={value ?? ""}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${fieldName}-error` : undefined}
        />
      </div>

      {error && (
        <div id={`${fieldName}-error`} className="flex items-center gap-1.5 text-sm text-red-600">
          <span className="text-red-500">⚠</span>
          {error}
        </div>
      )}
    </div>
  );
};

export default U2000InputTextBase;
